import { useState, useEffect, useCallback } from 'react';
import { usePanelLinkContext, LinkColor, SelectedEntity } from '../contexts/PanelLinkContext';

interface UsePanelLinkReturn {
  // Current link color
  linkColor: LinkColor;

  // Currently selected entity for this panel's color
  selectedEntity: SelectedEntity | null;

  // Change this panel's link color
  setLinkColor: (color: LinkColor) => void;

  // Broadcast a selection (notifies all same-color panels)
  selectEntity: (entity: SelectedEntity) => void;

  // Clear selection
  clearSelection: () => void;

  // Check if panel is linked
  isLinked: boolean;

  // Number of panels with same color
  linkedPanelsCount: number;
}

/**
 * Hook for panels to participate in the panel linking system.
 *
 * @param panelId - Unique identifier for this panel
 * @returns Object with link state and methods to interact with the system
 *
 * @example
 * ```tsx
 * const MyPanel = () => {
 *   const { selectedEntity, selectEntity, isLinked, linkColor } = usePanelLink('my-panel');
 *
 *   // React to selections from other linked panels
 *   useEffect(() => {
 *     if (selectedEntity?.type === 'country') {
 *       // Filter or highlight data for selected country
 *     }
 *   }, [selectedEntity]);
 *
 *   // Broadcast selection when user clicks
 *   const handleClick = (country) => {
 *     selectEntity({ type: 'country', id: country.id, label: country.name });
 *   };
 * };
 * ```
 */
export function usePanelLink(panelId: string): UsePanelLinkReturn {
  const {
    getPanelColor,
    setPanelColor,
    getColorSelection,
    broadcastSelection,
    clearColorSelection,
    getPanelsByColor,
    subscribeToColor,
  } = usePanelLinkContext();

  const linkColor = getPanelColor(panelId);
  const isLinked = linkColor !== 'none';

  // Local state for selected entity, synced with context
  const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | null>(
    () => getColorSelection(linkColor)
  );

  // Subscribe to color changes
  useEffect(() => {
    if (linkColor === 'none') {
      setSelectedEntity(null);
      return;
    }

    // Get initial selection
    setSelectedEntity(getColorSelection(linkColor));

    // Subscribe to updates
    const unsubscribe = subscribeToColor(linkColor, (entity) => {
      setSelectedEntity(entity);
    });

    return unsubscribe;
  }, [linkColor, getColorSelection, subscribeToColor]);

  // Set link color
  const setLinkColor = useCallback((color: LinkColor) => {
    setPanelColor(panelId, color);
  }, [panelId, setPanelColor]);

  // Broadcast selection
  const selectEntity = useCallback((entity: SelectedEntity) => {
    broadcastSelection(panelId, entity);
  }, [panelId, broadcastSelection]);

  // Clear selection
  const clearSelection = useCallback(() => {
    if (linkColor !== 'none') {
      clearColorSelection(linkColor);
    }
  }, [linkColor, clearColorSelection]);

  // Get count of linked panels
  const linkedPanelsCount = getPanelsByColor(linkColor).length;

  return {
    linkColor,
    selectedEntity,
    setLinkColor,
    selectEntity,
    clearSelection,
    isLinked,
    linkedPanelsCount,
  };
}

export default usePanelLink;
